/**
 * useSnapshotHistory Hook
 * Manages snapshot pagination for a specific series
 * (Level 3 - accessed ONLY through parent series)
 *
 * Groups snapshots by Year > Month for accordion display
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { reportSeriesAPI } from '../api.js';
import { formatDate } from '../i18n/format.js';
import { isFlagOn } from '../utils/dbFlags.js';

export function useSnapshotHistory(seriesId, options = {}) {
  const { i18n } = useTranslation();
  const { pageSize = 100, autoLoad = true } = options; // Load more to have full year data

  // State
  const [series, setSeries] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: pageSize,
    pages: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null); // Currently expanded year
  const [expandedMonths, setExpandedMonths] = useState(new Set()); // Expanded month keys

  // Load series info and snapshots (available for year-specific loading if needed)
  // eslint-disable-next-line no-unused-vars
  const _loadSnapshots = useCallback(async (year = null) => {
    if (!seriesId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await reportSeriesAPI.getSnapshots(seriesId, {
        page: 1,
        limit: pageSize,
        year: year
      });

      if (response.ok) {
        setSeries(response.data.series);
        setSnapshots(response.data.snapshots || []);
        setPagination(response.data.pagination || {
          total: 0,
          page: 1,
          limit: pageSize,
          pages: 0
        });
      } else {
        throw new Error(response.error?.message || 'Failed to load snapshots');
      }
    } catch (err) {
      console.error('Error loading snapshots:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [seriesId, pageSize]);

  // Load all snapshots initially (we need full data for year grouping)
  const loadAllYearData = useCallback(async () => {
    if (!seriesId) return;

    setLoading(true);
    setError(null);

    try {
      // Load without year filter to get all data
      const response = await reportSeriesAPI.getSnapshots(seriesId, {
        page: 1,
        limit: 500 // Get more snapshots for grouping
      });

      if (response.ok) {
        setSeries(response.data.series);
        setSnapshots(response.data.snapshots || []);
        setPagination(response.data.pagination || {
          total: 0,
          page: 1,
          limit: pageSize,
          pages: 0
        });
        // All years closed by default - user clicks to expand
        setSelectedYear(null);
      } else {
        throw new Error(response.error?.message || 'Failed to load snapshots');
      }
    } catch (err) {
      console.error('Error loading snapshots:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [seriesId, pageSize]);

  // Initial load
  useEffect(() => {
    if (autoLoad && seriesId) {
      loadAllYearData();
    }
  }, [autoLoad, seriesId, loadAllYearData]);

  // Group snapshots hierarchically: Year > Month > Snapshots
  const snapshotsByYear = useMemo(() => {
    const yearGroups = {};

    snapshots.forEach(snapshot => {
      const date = new Date(snapshot.as_of);
      const year = date.getFullYear();
      const monthKey = `${year}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = formatDate(date, { month: 'long' });

      // Initialize year group
      if (!yearGroups[year]) {
        yearGroups[year] = {
          year,
          months: {},
          totalSnapshots: 0,
          firstDate: null,
          lastDate: null
        };
      }

      // Initialize month group
      if (!yearGroups[year].months[monthKey]) {
        yearGroups[year].months[monthKey] = {
          key: monthKey,
          month: date.getMonth() + 1,
          label: monthLabel,
          year,
          snapshots: []
        };
      }

      // Add snapshot to month
      yearGroups[year].months[monthKey].snapshots.push(snapshot);
      yearGroups[year].totalSnapshots++;

      // Track date range
      if (!yearGroups[year].firstDate || date < yearGroups[year].firstDate) {
        yearGroups[year].firstDate = date;
      }
      if (!yearGroups[year].lastDate || date > yearGroups[year].lastDate) {
        yearGroups[year].lastDate = date;
      }
    });

    // Convert to sorted array
    return Object.values(yearGroups)
      .map(yearGroup => ({
        ...yearGroup,
        months: Object.values(yearGroup.months).sort((a, b) => b.key.localeCompare(a.key))
      }))
      .sort((a, b) => b.year - a.year);
  }, [snapshots, i18n.language]);

  // Get available years
  const availableYears = useMemo(() => {
    return snapshotsByYear.map(y => y.year);
  }, [snapshotsByYear]);

  // Toggle year expansion
  const toggleYear = useCallback((year) => {
    setSelectedYear(prev => prev === year ? null : year);
    // Clear expanded months when switching years
    setExpandedMonths(new Set());
  }, []);

  // Toggle month expansion
  const toggleMonth = useCallback((monthKey) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      if (next.has(monthKey)) {
        next.delete(monthKey);
      } else {
        next.add(monthKey);
      }
      return next;
    });
  }, []);

  // Expand all months in selected year
  const expandAllMonths = useCallback(() => {
    if (!selectedYear) return;
    const yearData = snapshotsByYear.find(y => y.year === selectedYear);
    if (yearData) {
      setExpandedMonths(new Set(yearData.months.map(m => m.key)));
    }
  }, [selectedYear, snapshotsByYear]);

  // Collapse all months
  const collapseAllMonths = useCallback(() => {
    setExpandedMonths(new Set());
  }, []);

  return {
    // Data
    series,
    snapshots,
    snapshotsByYear,
    pagination,
    loading,
    error,
    availableYears,
    selectedYear,
    expandedMonths,

    // Actions
    reload: loadAllYearData,
    toggleYear,
    toggleMonth,
    expandAllMonths,
    collapseAllMonths,
    setSelectedYear,

    // Helpers
    isYearExpanded: (year) => selectedYear === year,
    isMonthExpanded: (monthKey) => expandedMonths.has(monthKey),
    isLatest: (id) => isFlagOn(snapshots.find(s => s.id === id)?.is_latest)
  };
}

export default useSnapshotHistory;
